<template>
  <div class="free-trial-page">
    <!-- Hero Banner -->
    <section class="hero-banner">
      <div class="floating-shape shape-1"></div>
      <div class="floating-shape shape-2"></div>
      <div class="hero-content">
        <span class="hero-badge">限时活动</span>
        <h1 class="hero-title">免费试用 30 天</h1>
        <p class="hero-subtitle">
          任选3款产品，零成本体验完整功能，无需绑定信用卡
        </p>
        <button class="hero-cta" @click="showTrialModal">立即试用</button>
      </div>
    </section>

    <!-- Main Content -->
    <div class="page-container">
      <!-- Product Selection Grid -->
      <div class="product-grid">
        <div
          v-for="product in trialProducts"
          :key="product.id"
          class="product-card"
          :class="{ selected: selectedProducts.includes(product.id) }"
          @click="toggleProduct(product.id)"
        >
          <input
            type="checkbox"
            :checked="selectedProducts.includes(product.id)"
            class="product-checkbox"
            @click.stop
          />
          <div
            class="product-icon-wrapper"
            :style="{ background: product.bgColor }"
          >
            <Icon :icon="product.icon" :size="22" :color="product.color" />
          </div>
          <h4 class="product-name">{{ product.name }}</h4>
          <p class="product-desc">{{ product.desc }}</p>
          <span class="product-trial-tag">30天免费试用</span>
        </div>
      </div>

      <!-- Trial Process -->
      <section class="trial-process-section">
        <h3 class="process-title">试用流程</h3>
        <div class="process-steps">
          <div class="process-step-item">
            <div class="step-circle" style="background: #EBF5FF; color: #0066FF">1</div>
            <p class="step-label">选择产品</p>
            <p class="step-sublabel">任选3款试用</p>
          </div>
          <span class="step-arrow">→</span>
          <div class="process-step-item">
            <div class="step-circle" style="background: #FFFBEB; color: #FF9500">2</div>
            <p class="step-label">提交申请</p>
            <p class="step-sublabel">无需付款</p>
          </div>
          <span class="step-arrow">→</span>
          <div class="process-step-item">
            <div class="step-circle" style="background: #ECFDF5; color: #00B96B">3</div>
            <p class="step-label">立即开通</p>
            <p class="step-sublabel">3分钟部署</p>
          </div>
          <span class="step-arrow">→</span>
          <div class="process-step-item">
            <div class="step-circle" style="background: #F3E8FF; color: #7C3AED">4</div>
            <p class="step-label">体验30天</p>
            <p class="step-sublabel">决定是否续费</p>
          </div>
        </div>
      </section>

      <!-- Submit Button -->
      <div class="submit-section">
        <button
          class="submit-btn"
          :disabled="selectedProducts.length === 0"
          @click="submitTrial"
        >
          {{ selectedProducts.length > 0 ? `提交试用申请（已选${selectedProducts.length}款）` : '请选择试用产品' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { Icon } from '@/components/Icon'
import { trialProducts } from '../data/portalData'
import { usePortalState } from '../usePortalState'

defineOptions({ name: 'FreeTrialPage' })

const { showTrialModal } = usePortalState()
const selectedProducts = ref<string[]>([])

function toggleProduct(productId: string) {
  const index = selectedProducts.value.indexOf(productId)
  if (index > -1) {
    selectedProducts.value.splice(index, 1)
  } else {
    if (selectedProducts.value.length >= 3) {
      ElMessage.warning('最多选择3款产品')
      return
    }
    selectedProducts.value.push(productId)
  }
}

function submitTrial() {
  if (selectedProducts.value.length === 0) {
    ElMessage.warning('请至少选择一款产品')
    return
  }
  ElMessage.success('试用申请已提交！')
  selectedProducts.value = []
}
</script>

<style lang="scss" scoped>
.free-trial-page {
  padding-top: 64px;

  // Hero Banner
  .hero-banner {
    height: 300px;
    background: linear-gradient(135deg, #0066FF, #00B96B);
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .floating-shape {
    position: absolute;
    opacity: 0.08;

    &.shape-1 {
      top: 10%;
      left: 10%;
      width: 80px;
      height: 80px;
      background: #fff;
      border-radius: 24px;
      transform: rotate(15deg);
      animation: float1 6s ease-in-out infinite;
    }

    &.shape-2 {
      bottom: 20%;
      right: 15%;
      width: 120px;
      height: 120px;
      background: #fff;
      border-radius: 50%;
      animation: float2 8s ease-in-out infinite;
    }
  }

  @keyframes float1 {
    0%, 100% { transform: rotate(15deg) translateY(0); }
    50% { transform: rotate(15deg) translateY(-12px); }
  }

  @keyframes float2 {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-16px); }
  }

  .hero-content {
    position: relative;
    z-index: 2;
    color: #fff;
    max-width: 600px;
    padding: 0 24px;
  }

  .hero-badge {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
    font-size: 13px;
    padding: 6px 16px;
    border-radius: 20px;
    display: inline-block;
    margin-bottom: 16px;
  }

  .hero-title {
    font-size: 42px;
    font-weight: 900;
    margin: 0 0 12px;
  }

  .hero-subtitle {
    font-size: 17px;
    opacity: 0.85;
    margin: 0 0 28px;
  }

  .hero-cta {
    background: #fff;
    color: #0066FF;
    height: 48px;
    padding: 0 32px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    }
  }

  // Page Container
  .page-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 48px 24px;
  }

  // Product Grid
  .product-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 48px;

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .product-card {
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    padding: 20px;
    cursor: pointer;
    position: relative;
    background: #fff;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    }

    &.selected {
      border-color: #0066FF;
      box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.15);
    }
  }

  .product-checkbox {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #0066FF;
    pointer-events: none;
  }

  .product-icon-wrapper {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
  }

  .product-name {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 4px;
    color: #111827;
  }

  .product-desc {
    font-size: 13px;
    color: #6B7280;
    margin: 0;
  }

  .product-trial-tag {
    font-size: 12px;
    color: #00B96B;
    font-weight: 600;
    margin-top: 8px;
    display: inline-block;
  }

  // Trial Process
  .trial-process-section {
    text-align: center;
  }

  .process-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 24px;
    color: #111827;
  }

  .process-steps {
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .process-step-item {
    text-align: center;
    min-width: 80px;
  }

  .step-circle {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: 800;
    margin: 0 auto 12px;
  }

  .step-label {
    font-weight: 600;
    font-size: 14px;
    margin: 0 0 4px;
    color: #111827;
  }

  .step-sublabel {
    font-size: 12px;
    color: #6B7280;
    margin: 0;
  }

  .step-arrow {
    color: #D1D5DB;
    font-size: 28px;
    padding-top: 12px;

    @media (max-width: 768px) {
      display: none;
    }
  }

  // Submit Section
  .submit-section {
    text-align: center;
    margin-top: 48px;
  }

  .submit-btn {
    height: 48px;
    padding: 0 40px;
    background: #0066FF;
    color: #fff;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      background: #0044CC;
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0, 102, 255, 0.3);
    }

    &:disabled {
      background: #D1D5DB;
      cursor: not-allowed;
    }
  }
}
</style>
